import { queryOptions, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';
import { generateResponseScheme, type RespondedWith } from '@/models/response';
import { UserType } from '@/models/user/user-info';
import { fetcher } from '@/utils/fetcher';
import { fileSizeRegex, parseFileSize } from '@/utils/file-size';
import useUserInfo from '../users/use-user-info';

export const systemSettingQueryOptions = queryOptions({
  queryKey: ['/GetSystemSetting'],
  queryFn: ({ signal }) =>
    fetcher<RespondedWith<SystemSettingResponseType>>('/GetSystemSetting', undefined, {
      signal,
      schema: generateResponseScheme(systemSettingScheme),
      validResponseCodes: [0],
    }).then((response) => response?.Data),
  placeholderData: {
    fileSize: '10MB',
    TracesSampleRate: 0.01,
    ProfilesSampleRate: 0,
  },
  refetchOnWindowFocus: false,
  staleTime: 24 * 60 * 60 * 1000,
});

const BYTES_IN_MEGABYTE = 1_000_000;

export default function useFileSizeLimit(adminPermission?: boolean) {
  const query = useQuery(systemSettingQueryOptions);
  const { data: userInfo } = useUserInfo();

  const latestQueryDataRef = useRef(query.data);
  const latestLimitRef = useRef(parseFileSize(query.data?.fileSize ?? '10MB'));
  const latestUserInfoTypeRef = useRef(userInfo?.Type);

  const limit = useMemo(() => parseFileSize(query.data?.fileSize ?? '10MB'), [query.data]);

  useEffect(() => {
    latestQueryDataRef.current = query.data;
    latestLimitRef.current = parseFileSize(query.data?.fileSize ?? '10MB');
  }, [query.data]);

  useEffect(() => {
    latestUserInfoTypeRef.current = userInfo?.Type;
  }, [userInfo?.Type]);

  const getFileSizeLimit = (type: string | null | undefined): number => {
    const queryData = latestQueryDataRef.current;
    const latestLimit = latestLimitRef.current;

    if (!queryData || !('FileLimits' in queryData) || !queryData.FileLimits) return latestLimit;

    const isAdmin = adminPermission || latestUserInfoTypeRef.current === UserType.Admin;
    const { FileLimits: limits } = queryData;

    const limitMapping: Record<string, number> = {
      file: isAdmin ? limits.FileLimit : limits.UserFileLimit,
      video: isAdmin ? limits.VideoFileLimit : limits.UserVideoFileLimit,
      audio: isAdmin ? limits.AudioFileLimit : limits.UserAudioFileLimit,
      image: isAdmin ? limits.ImageFileLimit : limits.UserImageFileLimit,
      head: limits.HeadImageFileLimit,
    };

    return (limitMapping[type ?? ''] || latestLimit) * BYTES_IN_MEGABYTE;
  };

  return {
    limit,
    getFileSizeLimit,
    ...query,
  };
}

const fileLimitScheme = z.object({
  FileLimit: z.number(),
  VideoFileLimit: z.number(),
  AudioFileLimit: z.number(),
  ImageFileLimit: z.number(),
  UserFileLimit: z.number(),
  UserVideoFileLimit: z.number(),
  UserAudioFileLimit: z.number(),
  UserImageFileLimit: z.number(),
  HeadImageFileLimit: z.number(),
});

const systemSettingScheme = z.object({
  fileSize: z.string().regex(fileSizeRegex),
  FileLimits: fileLimitScheme.nullish(),
  ProfilesSampleRate: z.number(),
  TracesSampleRate: z.number(),
});

export type SystemSettingResponseType = z.infer<typeof systemSettingScheme>;
